import { CommonModule } from "@angular/common";
import {Component, computed, inject, input, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatCard } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTableModule } from "@angular/material/table";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import dayjs from "dayjs";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import {
  LorenMeteringType,
  LorenMeteringTypeTranslation,
} from "../../../loren-assets/interfaces/loren-metering-type";
import {
  LorenHomeAlarmsTableActionsComponent
} from "../../../loren-home/components/loren-home-alarms-table/components/loren-home-alarms-table-actions/loren-home-alarms-table-actions.component";
import {
  LorenAlarmsFilter
} from "../../../loren-home/components/loren-home-alarms-table/constants/loren-alarms-filters.constant";
import {
  LorenAlarmsSorting
} from "../../../loren-home/components/loren-home-alarms-table/constants/loren-alarms-sorting.constant";
import {
  LorenAlarmsColumns
} from "../../../loren-home/components/loren-home-alarms-table/constants/loren-alarms.columns.constants";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenMetricBinaryValues } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-icons";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../../shared/modules/loren-asset-card/constants/loren-metric-types.constants";
import { LorenMetricUnits } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-units.constants";
import { LorenFiltersComponent } from "../../../../shared/modules/loren-filters/loren-filters.component";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { LorenAlarmsService } from "../../../../shared/services/loren-alarms-service/loren-alarms.service";
import { Alarm } from "../../../alarm/alarm.service";
import {
  getFillLevelTranslationKey,
  isFillLevelSensor,
} from "../../../asset/fill-level.utils";
import { formatNumericValue } from "../../../asset/metric.utils";
import { MatButton } from "@angular/material/button";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: "loren-asset-details-alarms-table",
  imports: [
    CommonModule,
    FormsModule,
    MatIcon,
    MatTableModule,
    MatCard,
    MatMenuModule,
    MatProgressSpinner,
    TranslocoPipe,
    LorenDateModule,
    LorenFiltersComponent,
    LorenHomeAlarmsTableActionsComponent,
    MatButton,
  ],
  templateUrl: "./loren-asset-details-alarms-table.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-alarms-table.component.scss",
})
export class LorenAssetDetailsAlarmsTableComponent
  extends LorenBaseFeature
  implements OnInit, OnDestroy {
  private transloco = inject(TranslocoService);
  private alarmsService = inject(LorenAlarmsService);

  asset = input<LorenAsset>();

  @ViewChild("alarmsTable") alarmsTable;

  readonly alarmColumns = LorenAlarmsColumns;
  readonly columns = [
    "rowNum",
    this.alarmColumns.PARAMETER,
    this.alarmColumns.START,
    this.alarmColumns.STATUS,
  ];
  readonly initialFilters = {
    [LorenAlarmsFilter.ASSET_METERING_TYPE]: null,
    [LorenAlarmsFilter.STATUS]: null,
  };
  parametersFilter: { key: string; value: string }[];
  timeouts: ReturnType<typeof setTimeout>[] = [];
  loading = signal(false);
  alarms = signal([]);
  totalNumOfAlarms = signal(0);
  activeFilters = signal(this.initialFilters);
  sorted = signal({});
  alarmsError = signal(false);
  hasAlarmsToShow = computed(
    () =>
      this.alarms()?.length ||
      Object.values(this.activeFilters()).filter((v) => v).length
  );
  readonly alarmFilters = LorenAlarmsFilter;
  readonly startTimeFilters = [
    {
      key: this.transloco.translate("alarms.last_day"),
      value: this.prepareDateForFilters(24, "hour"),
    },
    {
      key: this.transloco.translate("alarms.last_week"),
      value: this.prepareDateForFilters(7, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_month"),
      value: this.prepareDateForFilters(30, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_half_year"),
      value: this.prepareDateForFilters(6, "month"),
    },
  ];
  readonly statusFilter = [
    { key: this.transloco.translate("alarms.active"), value: "ACTIVE" },
    { key: this.transloco.translate("alarms.inactive"), value: "INACTIVE" },
  ];

  ngOnInit() {
    this.parametersFilter = this.getParametersFilters();
    this.getAllAlarms();
  }

  ngOnDestroy() {
    this.timeouts.forEach((t) => clearTimeout(t));
  }

  alarmsIncreasedHandler($event: { scroll: number; numOfAlarms: number }) {
    const { scroll, numOfAlarms } = $event;
    const elNumToFocus = scroll * numOfAlarms - 1;
    this.loading.set(true);

    this.alarmsService
        .getAllAlarmsForAsset(scroll, numOfAlarms, this.asset().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.subscriptionNextHandler();
            this.alarms.update((v) => [...v, ...this.setAlarms(res.data)]);
            this.timeouts.push(
                setTimeout(() => {
                  this.scrollTo(elNumToFocus < 0 ? 0 : elNumToFocus);
                })
            );
          },
          error: () => this.subscriptionErrorHandler(),
        });
    this.sorted.set({});
  }

  alarmsDecreasedHandler($event: { scroll: number; numOfAlarms: number }) {
    const { scroll, numOfAlarms } = $event;
    const elNumToFocus = scroll ? scroll * numOfAlarms - 1 : 0;
    this.alarms.update((v) => v.slice(0, (scroll + 1) * numOfAlarms));
    this.scrollTo(elNumToFocus);
    this.sorted.set({});
  }

  selectionChangedHandler($event) {
    this.loading.set(true);
    this.alarmsService
        .getAllAlarmsForAsset(0, $event, this.asset().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.subscriptionNextHandler();
            this.alarms.set(this.setAlarms(res.data));
            this.totalNumOfAlarms.set(res.total);
            this.scrollTo(0);
          },
          error: () => this.subscriptionErrorHandler(),
        });
    this.sorted.set({});
  }

  scrollTo(el: number) {
    const tableRow = this.alarmsTable?._elementRef.nativeElement.children;
    const elToFocus = el ? tableRow[1].children?.[el] : tableRow?.[0];
    elToFocus?.scrollIntoView({ behavior: "smooth" });
    this.handleElFocus(el ? el + 1 : 0);
  }

  handleElFocus(el: number) {
    const tableRow =
      this.alarmsTable?._elementRef.nativeElement.children[1].children[el];
    tableRow?.classList.add("active");

    this.timeouts.push(
      setTimeout(() => {
        tableRow?.classList.remove("active");
      }, 1500)
    );
  }

  filter(filter, $event) {
    const filterValue = Array.from($event.keys())
      .filter((k) => $event.get(k))
      .join(",");
    this.activeFilters.update((v) => ({ ...v, [filter]: filterValue }));
    this.loading.set(true);
    this.alarmsService
        .getFilteredAlarmsForAsset(0, 20, this.asset().id, this.activeFilters())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.subscriptionNextHandler();
            this.alarms.set(this.setAlarms(res.data));
            this.totalNumOfAlarms.set(res.total);
            this.timeouts.push(setTimeout(() => this.scrollTo(0), 0));
          },
          error: () => this.subscriptionErrorHandler(),
        });
    this.sorted.set({});
  }

  clearAllFilters() {
    this.activeFilters.set(this.initialFilters);
    this.getAllAlarms();
    this.sorted.set({});
  }

  sort(column: string) {
    if (
      !this.sorted()[column] ||
      this.sorted()[column] === LorenAlarmsSorting.DESCENDING
    ) {
      this.alarms.update((v) =>
        [...v].sort((a, b) =>
          a[column] < b[column] ? -1 : a[column] > b[column] ? 1 : 0
        )
      );
      this.sorted.set({ [column]: LorenAlarmsSorting.ASCENDING });
      return;
    } else {
      this.alarms.update((v) =>
        [...v].sort((a, b) =>
          a[column] > b[column] ? -1 : a[column] < b[column] ? 1 : 0
        )
      );
      this.sorted.set({ [column]: LorenAlarmsSorting.DESCENDING });
      return;
    }
  }

  private setAlarms(alarms: Alarm[]) {
    return alarms.map((i) => ({
      parameter: i.meteringName,
      value: this.getValue(i.meteringType, i.meteringName, i.meteringValue),
      start: new Date(i.startedAt),
      status: i.status,
    }));
  }

  private getValue(type: LorenMeteringType, name: string, value) {
    switch (LorenMetricKinds.get(type)) {
      case LorenMetricCategories.BINARY:
        return this.transloco.translate(
          LorenMetricBinaryValues.get(type)[value]
        );
      case LorenMetricCategories.NUMERIC:
        if (isFillLevelSensor(type)) {
          const translationKey = getFillLevelTranslationKey(value);
          return translationKey ? this.transloco.translate(translationKey) : value;
        }

        return formatNumericValue(value) + LorenMetricUnits.get(type);
      case LorenMetricCategories.PERCENTAGE:
        if(!value) {
          return '';
        }

        return (Number(value) * 100).toFixed(2) + (LorenMetricUnits.get(type) ?? '');
      case LorenMetricCategories.TRACKING:
        if (name === 'LOW BATTERY') { // Hard-coded value in backend
          return Number(value);
        }

        return [
          value.find((d) => d.key === "lat")?.value,
          value.find((d) => d.key === "lng")?.value,
        ].join(", ");
      default:
        return value;
    }
  }

  private getAllAlarms() {
    this.loading.set(true);

    this.alarmsService
        .getAllAlarmsForAsset(0, 20, this.asset().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.subscriptionNextHandler();
            this.alarms.set(this.setAlarms(res.data));
            this.totalNumOfAlarms.set(res.total);
          },
          error: () => this.subscriptionErrorHandler(),
        });
  }

  private getParametersFilters() {
    const parametersSet = new Set([]);

    this.asset().metering.forEach((m) => parametersSet.add(m.type));
    return Array.from(parametersSet).map((p) => ({
      key: this.transloco.translate(LorenMeteringTypeTranslation.get(p)),
      value: p,
    }));
  }

  private prepareDateForFilters(value: number, unit: "hour" | "day" | "month") {
    return `${dayjs().subtract(value, unit).format("YYYY-MM-DD")}T00:00:00`;
  }

  private subscriptionNextHandler() {
    this.loading.set(false);
    this.alarmsError.set(false);
  }

  private subscriptionErrorHandler() {
    this.alarms.set([]);
    this.alarmsError.set(true);
    this.loading.set(false);
  }
}
